import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";

// The floating notifier's data. Reads the same BroadcastRecipient rows the admin
// mail module writes, so "read" follows the person between devices instead of
// living in one browser. Notifications are never removed here, only read/unread.

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

// The widget is hidden for the rest of the browser session, not forever — a new
// tab brings it back, which is what "hide for this session" should mean. Kept in
// sessionStorage deliberately: it is a per-tab convenience, not account state.
const SESSION_HIDE_KEY = "examfit.notifier.hidden";

const readSessionHidden = () => {
  try {
    return sessionStorage.getItem(SESSION_HIDE_KEY) === "1";
  } catch {
    return false;
  }
};

const writeSessionHidden = (hidden) => {
  try {
    if (hidden) sessionStorage.setItem(SESSION_HIDE_KEY, "1");
    else sessionStorage.removeItem(SESSION_HIDE_KEY);
  } catch {
    /* private window or blocked storage — the widget still works, it just
       forgets the preference on reload. */
  }
};

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  // The notification whose full message is open in the modal. Starts as the
  // list row, so the modal appears at once, and gains its formatted body when
  // that arrives.
  message: null,
  messageLoading: false,
  // Panel open/closed, and whether the widget has retreated to its edge tab.
  panelOpen: false,
  sessionHidden: readSessionHidden(),
};

export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/notifications`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to load notifications"
      );
    }
  }
);

// Marking seen is fire-and-forget on purpose: the badge has already updated
// locally, and a failed write only means the dot comes back on next load.
export const markNotificationSeen = createAsyncThunk(
  "notifications/markSeen",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiBaseUrl}/notifications/${id}`,
        { seen: true },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update");
    }
  }
);

// The whole message for one notification: the list only carries plain text.
export const fetchNotificationMessage = createAsyncThunk(
  "notifications/fetchMessage",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/notifications/${id}`, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to load the message");
    }
  }
);

// Puts a read notification back to unread, for something to come back to.
export const markNotificationUnseen = createAsyncThunk(
  "notifications/markUnseen",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${apiBaseUrl}/notifications/${id}`,
        { seen: false },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update");
    }
  }
);

export const markAllNotificationsSeen = createAsyncThunk(
  "notifications/markAllSeen",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/notifications/mark_all_seen`,
        {},
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update");
    }
  }
);

export const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    openNotifier: (state) => {
      state.panelOpen = true;
    },
    closeNotifier: (state) => {
      state.panelOpen = false;
    },
    toggleNotifier: (state) => {
      state.panelOpen = !state.panelOpen;
    },
    openMessage: (state, action) => {
      state.message = action.payload;
    },
    closeMessage: (state) => {
      state.message = null;
      state.messageLoading = false;
    },
    hideNotifierForSession: (state) => {
      state.panelOpen = false;
      state.sessionHidden = true;
      writeSessionHidden(true);
    },
    restoreNotifier: (state) => {
      state.sessionHidden = false;
      state.panelOpen = true;
      writeSessionHidden(false);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload?.notifications || [];
        state.unreadCount = action.payload?.unread_count || 0;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markNotificationSeen.pending, (state, action) => {
        // Optimistic: the dot clears as the row is tapped.
        const row = state.items.find((i) => i.id === action.meta.arg.id);
        if (row && !row.seen) {
          row.seen = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(fetchNotificationMessage.pending, (state) => {
        state.messageLoading = true;
      })
      .addCase(fetchNotificationMessage.fulfilled, (state, action) => {
        state.messageLoading = false;
        // Ignore an answer for a message that has since been closed or swapped.
        if (state.message?.id === action.payload?.id) {
          state.message = { ...state.message, ...action.payload };
        }
      })
      .addCase(fetchNotificationMessage.rejected, (state) => {
        state.messageLoading = false;
      })
      .addCase(markNotificationUnseen.pending, (state, action) => {
        const row = state.items.find((i) => i.id === action.meta.arg.id);
        if (row && row.seen) {
          row.seen = false;
          state.unreadCount += 1;
        }
      })
      .addCase(markAllNotificationsSeen.pending, (state) => {
        state.items = state.items.map((i) => ({ ...i, seen: true }));
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsSeen.fulfilled, (state) => {
        state.unreadCount = 0;
      });
  },
});

export const {
  openNotifier,
  closeNotifier,
  toggleNotifier,
  openMessage,
  closeMessage,
  hideNotifierForSession,
  restoreNotifier,
} = notificationSlice.actions;

export default notificationSlice.reducer;
